import type { DateOnly } from "@/shared/components/date-picker";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { sendCalendarPublishNotification } from "../repositories/CalendarRepository";

interface SendCalendarEmailParams {
	selectedEmails: string[];
	publisherNote?: string;
	fromDate: DateOnly;
	toDate: DateOnly;
	addRecipientsToTopic: boolean;
}

interface UseEmailCalendarParams {
	defaultAreaId?: string | number | null;
	onSuccess: () => void;
	onError: (error: Error) => void;
}

export const useEmailCalendar = ({
	defaultAreaId,
	onSuccess,
	onError,
}: UseEmailCalendarParams) => {
	const queryClient = useQueryClient();
	const [isOpen, setIsOpen] = useState(false);

	const mutation = useMutation({
		mutationFn: ({
			selectedEmails,
			publisherNote,
			fromDate,
			toDate,
			addRecipientsToTopic,
		}: SendCalendarEmailParams) =>
			sendCalendarPublishNotification({
				resource: "CALENDAR_PUBLISH",
				emailRecipients: selectedEmails.join(";"),
				publisherNote,
				fromDate,
				toDate,
				addRecipientsToTopic,
				defaultAreaId,
			}),
		onSuccess: async (_result, params) => {
			if (params.addRecipientsToTopic) {
				await queryClient.invalidateQueries({ queryKey: ["emailRecipients"] });
				await queryClient.invalidateQueries({ queryKey: ["emailAddresses"] });
			}
			setIsOpen(false);
			onSuccess();
		},
		onError,
	});

	return {
		isOpen,
		isPending: mutation.isPending,
		open: () => setIsOpen(true),
		close: () => {
			if (!mutation.isPending) setIsOpen(false);
		},
		send: mutation.mutate,
	};
};
